import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2 } from 'lucide-react';
import { catalog, catalogAdmin } from '../api/index.js';

const MIN_SEARCH = 2;

export function ContributorCombo({
  value,
  onChange,
  className = '',
  disabled,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data: selectedRow } = useQuery({
    queryKey: ['catalog-contributor-detail', value],
    queryFn: () => catalogAdmin.contributorDetail(value),
    enabled: Boolean(value),
    staleTime: 10 * 60 * 1000,
  });

  const selectedName =
    value && selectedRow && typeof (selectedRow as { name?: string }).name === 'string'
      ? (selectedRow as { name: string }).name
      : value || '';

  const searchEnough = dq.length >= MIN_SEARCH;
  const { data: searchData, isFetching } = useQuery({
    queryKey: ['catalog-contributors-search', dq],
    queryFn: () => catalog.contributors(dq),
    enabled: open && searchEnough,
    staleTime: 60 * 1000,
  });

  const rows = searchData?.contributors ?? [];

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const btnCls =
    'flex items-center justify-between gap-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-left bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:pointer-events-none';

  return (
    <div ref={rootRef} className={`relative min-w-0 flex-1 ${className}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        className={btnCls}
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span className="truncate min-w-0 flex-1 text-gray-900">
          {value ? selectedName : 'Search contributor…'}
        </span>
        <ChevronDown size={16} className="text-gray-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[14rem] max-w-lg rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 border-b border-gray-100 px-2">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Type ${MIN_SEARCH}+ letters`}
              className="flex-1 px-2 py-2 text-sm focus:outline-none"
              autoFocus
            />
            {isFetching && <Loader2 size={14} className="animate-spin text-gray-400 shrink-0" />}
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {!searchEnough ? (
              <p className="px-3 py-4 text-sm text-gray-400 text-center">
                Type at least {MIN_SEARCH} letters to search.
              </p>
            ) : rows.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-400 text-center">No contributors found.</p>
            ) : (
              rows.map((c) => (
                <button
                  key={c._id}
                  type="button"
                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-brand-50 ${
                    c._id === value ? 'bg-brand-50 font-medium' : ''
                  }`}
                  onClick={() => {
                    onChange(c._id);
                    setOpen(false);
                  }}
                >
                  <span className="block truncate text-gray-900">{c.name}</span>
                  <span className="block text-[11px] text-gray-400 font-mono truncate">{c._id}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
